// Bot / automation detection heuristics. Issue #852.
//
// Flag automated agents (WebDriver, headless browsers, Claude Chrome ext /
// Computer Use, generic crawlers) so suspicious survey submissions can be
// filtered downstream. Each signal is a heuristic — exposed individually
// for transparency, combined into `isBot`.
//
// NOTE: signals are evaluated at module load time. They do not change
// during a session, so a single snapshot is sufficient.

// WebDriver protocol active (Playwright, Puppeteer, Selenium, etc.).
// Strongest signal — modern automation frameworks set this flag.
export const isWebDriver = navigator.webdriver === true;

// Headless Chrome user agent string.
export const isHeadlessChrome = /HeadlessChrome/i.test(navigator.userAgent);

// Generic crawler/bot user agent patterns (GPTBot, ClaudeBot, googlebot,
// bingbot, etc.).
export const hasBotUserAgent =
  /bot|crawler|spider|crawling|headless|phantom|slurp|mediapartners/i.test(
    navigator.userAgent,
  );

// Stale / pre-Chrome-89 automation library globals.
export const hasAutomationGlobals = !!(
  window.callPhantom ||
  window._phantom ||
  window.__nightmare ||
  window.Cypress ||
  window.__webdriver_evaluate ||
  window.__selenium_evaluate ||
  window.__webdriver_script_function ||
  window.domAutomation ||
  window.domAutomationController
);

// Chromium UA but window.chrome missing — typical for stripped headless
// builds and some scraping setups.
export const hasChromiumMismatch =
  /Chrome\//.test(navigator.userAgent) && !window.chrome;

// `navigator.languages` exists but is empty — common in headless setups
// that didn't bother spoofing locale.
export const hasEmptyLanguages =
  Array.isArray(navigator.languages) && navigator.languages.length === 0;

// Combined verdict. True when ANY high-confidence signal fires.
// `hasChromiumMismatch` and `hasEmptyLanguages` are supporting signals only
// (require pairing) to avoid false positives on exotic real browsers.
export const isBot =
  isWebDriver ||
  isHeadlessChrome ||
  hasBotUserAgent ||
  hasAutomationGlobals ||
  (hasChromiumMismatch && hasEmptyLanguages);

// Convenience block for embedding in client-info payloads.
export const bot = {
  isBot,
  isWebDriver,
  isHeadlessChrome,
  hasBotUserAgent,
  hasAutomationGlobals,
  hasChromiumMismatch,
  hasEmptyLanguages,
};
